import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Switch,
  Typography,
} from '@mui/material';
import { colors } from '../styles';
import { captureLinkTokenFromUrl } from '../utils/sensitiveLinkToken';
import {
  CATEGORY_COPY,
  NOTE_COPY,
  errorCode,
  readPreferences,
  saveMarketing,
} from '../services/emailPreferences';

const STATUS = { loading: 'loading', ready: 'ready', invalid: 'invalid', failed: 'failed' };

const CategoryRow = ({ category, saving, onToggle }) => {
  const copy = CATEGORY_COPY[category.key];
  if (!copy) return null;
  const labelId = `email-pref-${category.key}`;
  return (
    <Box
      sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, py: 2 }}
      data-testid={`category-${category.key}`}
    >
      <Box>
        <Typography id={labelId} sx={{ fontWeight: 700, color: colors.secondary }}>
          {copy.title}
        </Typography>
        <Typography variant="body2" sx={{ color: colors.text.secondary, mt: 0.5 }}>
          {copy.description}
        </Typography>
        {category.note && NOTE_COPY[category.note] && (
          <Typography variant="body2" sx={{ color: colors.text.secondary, mt: 1 }}>
            {NOTE_COPY[category.note]}
          </Typography>
        )}
      </Box>
      <Switch
        checked={category.enabled}
        disabled={!category.editable || saving}
        onChange={(event) => onToggle(category.key, event.target.checked)}
        inputProps={{ 'aria-labelledby': labelId, 'aria-label': copy.title }}
      />
    </Box>
  );
};

const EmailPreferences = () => {
  const [searchParams] = useSearchParams();
  // index.js already moved the token into memory; capture again in case the
  // page was reached by client-side navigation.
  const [token] = useState(() => captureLinkTokenFromUrl() || searchParams.get('token') || '');
  const [status, setStatus] = useState(token ? STATUS.loading : STATUS.invalid);
  const [preferences, setPreferences] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    readPreferences(token)
      .then((data) => {
        if (cancelled) return;
        setPreferences(data);
        setStatus(STATUS.ready);
      })
      .catch((error) => {
        if (cancelled) return;
        setStatus(error?.response?.status === 400 ? STATUS.invalid : STATUS.failed);
      });
    return () => { cancelled = true; };
  }, [token]);

  const save = useCallback(async (marketing) => {
    setSaving(true);
    setMessage(null);
    try {
      const data = await saveMarketing(token, marketing);
      setPreferences(data);
      setMessage({
        severity: 'success',
        text: marketing ? 'Saved. Offers and news are on.' : 'Saved. You are unsubscribed from offers and news.',
      });
    } catch (error) {
      const code = errorCode(error);
      if (error?.response?.data?.categories) setPreferences(error.response.data);
      setMessage({
        severity: 'error',
        text: code === 'DELIVERY_BLOCKED' || code === 'NO_ACCOUNT'
          ? NOTE_COPY[code === 'DELIVERY_BLOCKED' ? 'delivery_blocked' : 'no_account']
          : 'That did not save. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  }, [token]);

  const marketing = preferences?.categories?.find((entry) => entry.key === 'marketing');

  return (
    <>
      <Helmet>
        <title>Email preferences · Budgit</title>
        <meta name="robots" content="noindex" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>
      <Box sx={{ minHeight: '100vh', backgroundColor: colors.background, py: { xs: 4, md: 8 } }}>
        <Container maxWidth="sm">
          <Typography variant="h4" sx={{ fontWeight: 900, color: colors.primary, mb: 1 }}>
            Email preferences
          </Typography>
          {status === STATUS.ready && (
            <Box sx={{ mb: 3 }} data-testid="masked-email">
              <Typography sx={{ color: colors.secondary }}>
                {preferences?.email ? `For ${preferences.email}` : 'For the address this link was sent to'}
              </Typography>
            </Box>
          )}

          {status === STATUS.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress aria-label="Loading preferences" />
            </Box>
          )}

          {status === STATUS.invalid && (
            <Alert severity="warning">
              This link is not valid or has expired. Use the link in a recent Budgit email, or
              write to support@budgit.app.
            </Alert>
          )}

          {status === STATUS.failed && (
            <Alert severity="error">We could not load your preferences. Please try again later.</Alert>
          )}

          {status === STATUS.ready && (
            <>
              <Paper sx={{ borderRadius: '12px', px: 3, py: 1, boxShadow: 'none', border: `1px solid ${colors.secondary}14` }}>
                {preferences.categories.map((category) => (
                  <CategoryRow
                    key={category.key}
                    category={category}
                    saving={saving}
                    onToggle={(_, checked) => save(checked)}
                  />
                ))}
              </Paper>

              {message && (
                <Alert severity={message.severity} sx={{ mt: 2 }} role="status">
                  {message.text}
                </Alert>
              )}

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={saving || !marketing || !marketing.enabled}
                  onClick={() => save(false)}
                  sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 700 }}
                >
                  Unsubscribe from all marketing email
                </Button>
              </Box>

              <Typography variant="body2" sx={{ color: colors.text.secondary, mt: 3 }}>
                Changes save right away. No sign-in needed. Questions? support@budgit.app
              </Typography>
            </>
          )}
        </Container>
      </Box>
    </>
  );
};

export default EmailPreferences;
